/**
 * Renders a report artifact: period + "Revision N", huge H1, publish time, limitations, then every
 * ArtifactSection kind (summary, callout, kpis, table, chart, recommendations, markdown) and the
 * `changesMade` footer. Every number comes from the artifact (computed server-side).
 */
import { ArrowDown, ArrowUp, ChevronDown, CircleAlert, MessageCircleQuestion, Play, ShieldCheck } from "lucide-react";
import { useState, type ReactNode } from "react";
import { openAsk } from "@/api/ask-store";
import type { Artifact, ArtifactSection } from "@/api/chat";
import { useRunTool } from "@/api/hooks";
import { cn } from "@/lib/cn";
import { shortDate } from "@/lib/format";
import { Button } from "../../ui/Button";
import { PopoverPanel, usePopover } from "../../ui/Overlay";
import { Markdown } from "../Markdown";
import { fullTimeLabel } from "../util";
import { deltaDirection, DIRECTION_CLASS, formatDelta, formatDeltaPct, formatKpi } from "./fmt";
import { ReportChart } from "./ReportChart";
import { ReportTable } from "./ReportTable";

export function sectionTitle(s: ArtifactSection, i: number): string {
  switch (s.kind) {
    case "summary":
      return s.label || "Summary";
    case "callout":
      return "Callout";
    case "kpis":
      return "Key metrics";
    case "table":
    case "chart":
      return s.title || (s.kind === "table" ? "Table" : "Chart");
    case "recommendations":
      return s.title || "Recommendations";
    case "markdown":
      return s.title || firstLine(s.markdown) || `Section ${i + 1}`;
    default:
      return `Section ${i + 1}`;
  }
}

function firstLine(md: string): string {
  const l = (md ?? "").split("\n").find((x) => x.trim()) ?? "";
  return l.replace(/[#*_`>]/g, "").trim().slice(0, 60);
}

function periodLabel(a: Artifact): string | null {
  if (!a.periodStart && !a.periodEnd) return null;
  const f = (d?: string | null) => (d ? shortDate(`${d.slice(0, 10)}T12:00:00`) : "…");
  return `${f(a.periodStart)} — ${f(a.periodEnd)}`;
}

export function ReportBody({
  artifact,
  compact,
  animate = true,
  onRevision,
}: {
  artifact: Artifact;
  compact?: boolean;
  animate?: boolean;
  onRevision?: (id: string) => void;
}) {
  const period = periodLabel(artifact);
  return (
    <article className="flex flex-col gap-4">
      <header className={cn("pb-2", compact ? "pt-1" : "pt-3")}>
        <div className="flex flex-wrap items-center gap-2 text-[13px] text-muted">
          {period && <span>{period}</span>}
          <RevisionChip artifact={artifact} onRevision={onRevision} />
        </div>
        <h1 className={cn("mt-3 font-bold leading-[1.12] tracking-[-0.025em] text-balance", compact ? "text-[26px]" : "text-[34px] sm:text-[38px]")}>{artifact.title}</h1>
        <div className="mt-3 text-[13px] text-muted">
          {artifact.publishedAt && <>Published {fullTimeLabel(artifact.publishedAt)}</>}
          {artifact.comparison && <span className="ml-2 text-subtle">· Compared {artifact.comparison.replace(/^vs\.?\s*/i, "with ")}</span>}
        </div>
      </header>

      {artifact.limitations.length > 0 && (
        <SectionCard className="scroll-mt-20" dataSection={-1}>
          <h2 className="mb-2 flex items-center gap-2 text-[14px] font-semibold">
            <CircleAlert className="size-4 text-amber" />
            Limitations
          </h2>
          <ul className="list-disc space-y-1.5 pl-5 text-[13.5px] leading-relaxed text-fg/80 marker:text-subtle">
            {artifact.limitations.map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </SectionCard>
      )}

      {artifact.sections.map((s, i) => (
        <div key={i} data-section={i} className="scroll-mt-20">
          <Section section={s} brandId={artifact.brandId} animate={animate} artifact={artifact} index={i} />
        </div>
      ))}

      {!artifact.sections.length && <SectionCard className="text-center text-[13px] text-muted">This report has no sections.</SectionCard>}

      <footer className="mt-2 flex items-start gap-2 border-t border-line pt-4 text-[13px] text-muted">
        <ShieldCheck className="mt-0.5 size-4 shrink-0 text-success" />
        <span>{artifact.changesMade || "No ads, budgets, schedules, or other account settings were changed."}</span>
      </footer>
    </article>
  );
}

function RevisionChip({ artifact, onRevision }: { artifact: Artifact; onRevision?: (id: string) => void }) {
  const p = usePopover();
  const revs = artifact.revisions ?? [];
  const label = `Revision ${artifact.revision ?? 1}`;
  if (revs.length <= 1 || !onRevision) return <span className="inline-flex h-6 items-center rounded-full border border-line-strong px-2.5 text-[12px] font-medium text-fg/85">{label}</span>;
  return (
    <>
      <button
        type="button"
        ref={(el) => {
          p.anchorRef.current = el;
        }}
        onClick={p.toggle}
        aria-expanded={p.open}
        className="inline-flex h-6 items-center gap-1 rounded-full border border-line-strong px-2.5 text-[12px] font-medium text-fg/85 hover:bg-hover"
      >
        {label}
        <ChevronDown className="size-3" />
      </button>
      <PopoverPanel open={p.open} onClose={p.close} anchorRef={p.anchorRef} className="w-56 p-1.5">
        {[...revs].reverse().map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => {
              p.close();
              onRevision(r.id);
            }}
            className={cn("flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-[13px] hover:bg-hover", r.id === artifact.id && "bg-elevated")}
          >
            <span className="font-medium">Revision {r.revision}</span>
            <span className="text-[11.5px] text-muted">{r.publishedAt ? shortDate(r.publishedAt) : ""}</span>
          </button>
        ))}
      </PopoverPanel>
    </>
  );
}

function SectionCard({ children, className, dataSection }: { children: ReactNode; className?: string; dataSection?: number }) {
  return (
    <section data-section={dataSection} className={cn("rounded-2xl border border-line bg-surface p-5", className)}>
      {children}
    </section>
  );
}

function Section({ section: s, brandId, animate, artifact, index }: { section: ArtifactSection; brandId?: string; animate: boolean; artifact: Artifact; index: number }) {
  switch (s.kind) {
    case "summary":
      return (
        <SectionCard>
          <div className="mb-2 text-[11.5px] font-semibold uppercase tracking-[0.1em] text-lavender">{s.label}</div>
          <Markdown text={s.markdown} className="text-[14.5px] text-fg/90" />
        </SectionCard>
      );
    case "callout":
      return (
        <div
          className={cn(
            "rounded-r-xl border-l-[3px] bg-surface-2 px-5 py-4",
            s.tone === "positive" ? "border-success" : s.tone === "negative" ? "border-danger" : "border-line-strong",
          )}
        >
          <Markdown text={s.markdown} className="text-[14.5px] text-fg/85" />
        </div>
      );
    case "kpis":
      return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {s.items.map((k, i) => (
            <KpiCard key={i} item={k} />
          ))}
        </div>
      );
    case "table":
      return <ReportTable title={s.title} columns={s.columns} rows={s.rows} comparisonLabel={s.comparisonLabel ?? artifact.comparison ?? undefined} />;
    case "chart":
      return (
        <SectionCard>
          <h3 className="mb-3 text-[16px] font-semibold tracking-[-0.01em]">{s.title}</h3>
          <ReportChart spec={s} animate={animate} />
        </SectionCard>
      );
    case "recommendations":
      return <Recommendations section={s} brandId={brandId} artifact={artifact} index={index} />;
    case "markdown":
      return (
        <SectionCard>
          {s.title && <h3 className="mb-2 text-[17px] font-semibold tracking-[-0.01em]">{s.title}</h3>}
          <Markdown text={s.markdown} className="text-[14.5px] text-fg/90" />
        </SectionCard>
      );
    default:
      return null;
  }
}

type Kpi = Extract<ArtifactSection, { kind: "kpis" }>["items"][number];

function KpiCard({ item: k }: { item: Kpi }) {
  // Spend movement is neutral (PlayAd greys it); other KPIs use the server's `better` direction.
  const spendLike = /^spend$/i.test(String(k.label ?? "").trim());
  const dir = deltaDirection(k.delta ?? k.deltaPct ?? null, spendLike ? { key: "spend" } : { better: k.better });
  const up = (k.delta ?? k.deltaPct ?? 0) > 0;
  const hasDelta = typeof k.delta === "number" || typeof k.deltaPct === "number";
  return (
    <div className="flex flex-col rounded-2xl border border-line bg-surface-2 p-4">
      <div className="text-[13px] leading-snug text-muted [overflow-wrap:anywhere]">{k.label}</div>
      <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-[24px] font-semibold tracking-[-0.01em] tnum">{formatKpi(k.value, k.format)}</span>
        {hasDelta && (
          <span className={cn("inline-flex items-center gap-0.5 text-[13px] font-medium tnum", DIRECTION_CLASS[dir])}>
            {up ? <ArrowUp className="size-3.5" /> : <ArrowDown className="size-3.5" />}
            {typeof k.delta === "number" ? formatDelta(k.delta, k.format) : ""}
            {typeof k.deltaPct === "number" ? ` (${formatDeltaPct(k.deltaPct)})` : ""}
          </span>
        )}
      </div>
      <div className="mt-1.5 text-[12px] text-subtle">{k.comparison}</div>
      {k.spark && k.spark.length > 1 && <Sparkline values={k.spark} tone={dir} className="mt-3" />}
    </div>
  );
}

export function Sparkline({ values, tone = "neutral", className, height = 36 }: { values: number[]; tone?: "good" | "bad" | "neutral"; className?: string; height?: number }) {
  const nums = values.filter((v) => typeof v === "number" && Number.isFinite(v));
  if (nums.length < 2) return null;
  const w = 200;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min || 1;
  const pts = nums.map((v, i) => `${((i / (nums.length - 1)) * w).toFixed(1)},${(height - 3 - ((v - min) / span) * (height - 6)).toFixed(1)}`).join(" ");
  const color = tone === "good" ? "var(--success)" : tone === "bad" ? "var(--danger)" : "#8a8a90";
  return (
    <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" className={cn("h-9 w-full overflow-visible", className)} aria-hidden>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

type RecSection = Extract<ArtifactSection, { kind: "recommendations" }>;

function Recommendations({ section, brandId, artifact, index }: { section: RecSection; brandId?: string; artifact: Artifact; index: number }) {
  return (
    <div className="flex flex-col gap-3">
      {section.title && <h3 className="px-1 text-[17px] font-semibold tracking-[-0.01em]">{section.title}</h3>}
      {section.items.map((it, i) => (
        <RecommendationRows key={i} item={it} brandId={brandId} artifact={artifact} sectionIndex={index} />
      ))}
    </div>
  );
}

export function RecommendationRows({
  item,
  brandId,
  artifact,
  sectionIndex,
}: {
  item: RecSection["items"][number];
  brandId?: string | null;
  artifact?: Artifact;
  sectionIndex?: number;
}) {
  const runTool = useRunTool();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const rows: [string, string | undefined][] = [
    ["Target", item.target],
    ["Action", item.action],
    ["Success", item.success],
    ["Guardrail", item.guardrail],
    ["Review date", item.reviewDate],
  ];
  const run = async () => {
    if (!item.tool) return;
    setBusy(true);
    try {
      const input = { ...item.tool.input };
      if (brandId && !("brandId" in input)) input.brandId = brandId;
      const r = await runTool(item.tool.name, input, { label: item.action.slice(0, 80), success: "Done" });
      if (r !== undefined) setDone(true);
    } catch {
      /* toasted by useRunTool */
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-2xl border border-line bg-surface px-5">
      {rows
        .filter(([, v]) => v)
        .map(([k, v]) => (
          <div key={k} className="grid grid-cols-[96px_1fr] gap-4 border-b border-line/70 py-4 last:border-0 sm:grid-cols-[120px_1fr]">
            <div className="text-[13px] font-semibold text-fg/90">{k}</div>
            <div className="text-[13.5px] leading-relaxed text-fg/80">{v}</div>
          </div>
        ))}
      {(item.tool || artifact) && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line/70 py-3">
          {artifact && (
            <Button
              size="xs"
              variant="ghost"
              icon={<MessageCircleQuestion className="size-3.5" />}
              onClick={() =>
                openAsk({
                  title: "Ask about this recommendation",
                  prompt: `About the recommendation for "${item.target}" in "${artifact.title}": `,
                  context: { artifactId: artifact.id, section: sectionIndex, recommendation: item },
                })
              }
            >
              Ask Mimic
            </Button>
          )}
          {item.tool && (
            <Button size="xs" variant={done ? "secondary" : "primary"} icon={<Play className="size-3" />} loading={busy} disabled={done} onClick={() => void run()} title={item.tool.name}>
              {done ? "Done" : "Run"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
